import { useEffect, useState } from 'react';
import moment from 'moment';

// Live progress of uploads running in this tab, keyed by upload_id
let uploads = {};
const listeners = new Set();

export const LIVE_STATUS = { UPLOADING: 'UPLOADING', PAUSED: 'PAUSED', DONE: 'DONE' };

// Warn before closing the tab while an upload is running
const warnBeforeUnload = (evt) => {
  evt.preventDefault();
  evt.returnValue = '';
};
let unloadWarningOn = false;
const syncUnloadWarning = () => {
  const uploading = Object.values(uploads).some(upload => upload.status === LIVE_STATUS.UPLOADING || upload.status === LIVE_STATUS.PAUSED);
  if (uploading === unloadWarningOn) return;
  unloadWarningOn = uploading;
  if (uploading) window.addEventListener('beforeunload', warnBeforeUnload);
  else window.removeEventListener('beforeunload', warnBeforeUnload);
};

export const updateLiveUpload = (uploadId, data) => {
  uploads = { ...uploads, [uploadId]: { ...uploads[uploadId], ...data, uploadId } };
  syncUnloadWarning();
  listeners.forEach(listener => listener(uploads));
};

export const useLiveUploads = () => {
  const [state, setState] = useState(uploads);
  useEffect(() => {
    setState(uploads);
    listeners.add(setState);
    return () => listeners.delete(setState);
  }, []);
  return state;
};

// One Web Lock per upload_id while a tab uploads it, so other tabs know it's running and can't start it too
const LOCK_PREFIX = 'stream-upload:';
const locksSupported = () => typeof navigator !== 'undefined' && !!navigator.locks;

// Runs task() while holding the session's lock; resolves onBusy() instead if another tab holds it
export const withUploadLock = (uploadId, task, onBusy) => {
  if (!locksSupported()) return task();
  return navigator.locks.request(LOCK_PREFIX + uploadId, { ifAvailable: true }, lock => (lock ? task() : onBusy()));
};

// upload_ids being uploaded by any tab of this browser
export const getLockedUploadIds = () => {
  if (!locksSupported()) return Promise.resolve(new Set());
  return navigator.locks.query()
    .then(({ held = [] }) => new Set(held.map(lock => lock.name).filter(name => name && name.startsWith(LOCK_PREFIX)).map(name => name.slice(LOCK_PREFIX.length))))
    .catch(() => new Set());
};

// Activity within this window means someone is still uploading (heartbeats run every 30 s)
const ACTIVITY_WINDOW_MINUTES = 1;
export const UNFINISHED_FILE_STATUSES = ['PENDING', 'UPLOADING'];

export const isRecentActivity = (timestamp) => {
  // moment(undefined) would count as now
  if (!timestamp) return false;
  const at = moment(timestamp);
  return at.isValid() && moment().diff(at, 'minutes', true) < ACTIVITY_WINDOW_MINUTES;
};

// navigator.onLine catches a lost connection, not every outage
const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

export const waitForOnline = () => (isOnline() ? Promise.resolve() : new Promise(resolve => {
  window.addEventListener('online', () => resolve(), { once: true });
}));

export const useOnlineStatus = () => {
  const [online, setOnline] = useState(isOnline);
  useEffect(() => {
    const update = () => setOnline(isOnline());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
};
